/**
 * Generador de contraseñas y passphrases con aleatoriedad criptográfica.
 *
 * `randomInt` usa rechazo de módulo sobre `crypto.getRandomValues` para no
 * introducir sesgo. La entropía se calcula y devuelve para poder mostrarla y
 * para los tests.
 */

import { DEFAULT_WORDLIST } from "./wordlist";

const LOWER = "abcdefghijklmnopqrstuvwxyz";
const UPPER = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const DIGITS = "0123456789";
const SYMBOLS = "!@#$%^&*()-_=+[]{};:,.<>?";
const AMBIGUOUS = new Set("Il1O0o");

export interface PasswordOptions {
  readonly length?: number;
  readonly lowercase?: boolean;
  readonly uppercase?: boolean;
  readonly digits?: boolean;
  readonly symbols?: boolean;
  readonly avoidAmbiguous?: boolean;
}

/** Entero uniforme en [0, max) sin sesgo de módulo. */
export function randomInt(max: number): number {
  if (max <= 0) {
    throw new Error("max must be > 0");
  }
  const limit = Math.floor(0xffff_ffff / max) * max;
  const buf = new Uint32Array(1);
  let x: number;
  do {
    crypto.getRandomValues(buf);
    x = buf[0] ?? 0;
  } while (x >= limit);
  return x % max;
}

function pick(alphabet: string): string {
  return alphabet[randomInt(alphabet.length)] ?? alphabet[0] ?? "";
}

export interface GeneratedSecret {
  readonly value: string;
  readonly entropyBits: number;
}

export function generatePassword(options: PasswordOptions = {}): GeneratedSecret {
  const {
    length = 20,
    lowercase = true,
    uppercase = true,
    digits = true,
    symbols = true,
    avoidAmbiguous = true,
  } = options;

  const pools: string[] = [];
  if (lowercase) {
    pools.push(LOWER);
  }
  if (uppercase) {
    pools.push(UPPER);
  }
  if (digits) {
    pools.push(DIGITS);
  }
  if (symbols) {
    pools.push(SYMBOLS);
  }
  if (pools.length === 0) {
    throw new Error("at least one character class required");
  }

  let alphabet = pools.join("");
  if (avoidAmbiguous) {
    alphabet = [...alphabet].filter((c) => !AMBIGUOUS.has(c)).join("");
  }
  if (length < pools.length) {
    throw new Error("length shorter than required character classes");
  }

  const chars: string[] = [];
  // Garantiza al menos un carácter de cada clase pedida.
  for (const pool of pools) {
    const filtered = avoidAmbiguous ? [...pool].filter((c) => !AMBIGUOUS.has(c)).join("") : pool;
    chars.push(pick(filtered));
  }
  while (chars.length < length) {
    chars.push(pick(alphabet));
  }

  // Barajado Fisher–Yates con RNG seguro (elimina el sesgo posicional).
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomInt(i + 1);
    const tmp = chars[i]!;
    chars[i] = chars[j]!;
    chars[j] = tmp;
  }

  const entropyBits = length * Math.log2(alphabet.length);
  return { value: chars.join(""), entropyBits };
}

export interface PassphraseOptions {
  readonly words?: number;
  readonly separator?: string;
  readonly capitalize?: boolean;
  readonly includeNumber?: boolean;
  /** Lista de palabras a inyectar (p. ej. la lista EFF completa). */
  readonly wordlist?: readonly string[];
}

export function generatePassphrase(options: PassphraseOptions = {}): GeneratedSecret {
  const {
    words = 5,
    separator = "-",
    capitalize = true,
    includeNumber = true,
    wordlist = DEFAULT_WORDLIST,
  } = options;
  if (wordlist.length < 2) {
    throw new Error("wordlist too small");
  }

  const chosen: string[] = [];
  for (let i = 0; i < words; i++) {
    let word = wordlist[randomInt(wordlist.length)] ?? wordlist[0]!;
    if (capitalize) {
      word = (word[0] ?? "").toUpperCase() + word.slice(1);
    }
    chosen.push(word);
  }
  let value = chosen.join(separator);
  let entropyBits = words * Math.log2(wordlist.length);
  if (includeNumber) {
    value += separator + randomInt(10_000).toString().padStart(4, "0");
    entropyBits += Math.log2(10_000);
  }
  return { value, entropyBits };
}
